import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'use-context-selector';

import {
  API_ENDPOINT_KEYS,
  QUERY_KEY,
  SERVICE_AUTH_ENDPOINTS,
  SERVICE_RENTAL_ENDPOINTS,
} from '~common';
import { getAuthRequest } from '~services/auth.service';
import { getRequest } from '~services/client.service';
import { inMemoryJWTService } from '~services/jwt.service';
import { LoggedInUserInfo, UserGroups } from '~types';

type LoggedInUserInfoContextType = {
  loggedInUserInfo: LoggedInUserInfo | null;
};

const LoggedInUserInfoContext = createContext<LoggedInUserInfoContextType>({
  loggedInUserInfo: null,
});

type Props = {
  children: React.ReactNode;
};

const mergeUserInfo = (
  userInfoFromAuth: Partial<LoggedInUserInfo>,
  userInfoFromRental: Partial<LoggedInUserInfo>,
): LoggedInUserInfo => {
  return {
    ...userInfoFromRental,
    roles: userInfoFromAuth.roles || [],
    role: userInfoFromAuth.role || userInfoFromRental.role,
    fuzedashPermissions: userInfoFromAuth?.fuzedashPermissions ?? [],
  } as LoggedInUserInfo;
};

const getLoggedInUserInfo = async (): Promise<LoggedInUserInfo> => {
  const userInfoFromRental: Partial<LoggedInUserInfo> = await getRequest({
    path: SERVICE_RENTAL_ENDPOINTS[API_ENDPOINT_KEYS.GET_PROFILE],
  });
  const userInfoFromAuth: Partial<LoggedInUserInfo> = await getAuthRequest({
    path: SERVICE_AUTH_ENDPOINTS[API_ENDPOINT_KEYS.GET_PROFILE],
  });

  return mergeUserInfo(userInfoFromAuth, userInfoFromRental);
};

export const LoggedInUserInfoProvider: React.FC<Props> = ({ children }) => {
  const getUserGroups = async (): Promise<UserGroups> =>
    getRequest({ path: 'api/user/me/groups' });

  const [loggedInUserInfo, setLoggedInUserInfo] =
    useState<LoggedInUserInfo | null>(null);

  // Tracks whether the fetched user info has been pushed into
  // inMemoryJWTService. We only render children once this is true so that
  // code calling inMemoryJWTService.getUserInfo() outside of React (e.g. in
  // request interceptors/utilities) never observes a null/stale value.
  const [isUserInfoSynced, setIsUserInfoSynced] = useState(false);

  useQuery<LoggedInUserInfo>([QUERY_KEY.GET_LOGGED_IN_USER_INFO], () =>
    getLoggedInUserInfo().then((data) => {
      // append the user info to current stored info,
      // not override it because it might already have group info (returned by query GET_GROUP_USERS)
      setLoggedInUserInfo((prev) => ({
        ...prev,
        ...data,
      }));
      return data;
    }),
  );

  useQuery<UserGroups>(
    [QUERY_KEY.GET_GROUP_USERS],
    () =>
      getUserGroups().then((data) => {
        // useLoggedInUserInfoContext is used in many components, if we create new from this component it's very risky
        // so I update this code to override property of loggedInUserInfo
        setLoggedInUserInfo((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            groups: data.data,
          } as LoggedInUserInfo;
        });
        return data;
      }),
    {
      enabled: !!loggedInUserInfo,
    },
  );

  useEffect(() => {
    if (loggedInUserInfo) {
      inMemoryJWTService.setUserInfo(loggedInUserInfo);
      // Flip the gate only after the service has been populated so children
      // (and any getUserInfo() call they trigger) never mount beforehand.
      setIsUserInfoSynced(true);
    }
  }, [loggedInUserInfo]);

  return (
    <LoggedInUserInfoContext.Provider
      value={{
        loggedInUserInfo,
      }}
    >
      {/*
        Hold children back until the fetched user info has been synced into
        inMemoryJWTService, so code calling getUserInfo() outside of React
        never observes a null/stale value. This provider only mounts behind
        RequireAuth, so a token always exists and the query resolves.
      */}
      {isUserInfoSynced ? (
        children
      ) : (
        <Box
          display="flex"
          width="100%"
          height="100dvh"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      )}
    </LoggedInUserInfoContext.Provider>
  );
};

export const useLoggedInUserInfoContext = () =>
  useContext(LoggedInUserInfoContext);
